import React from 'react';
import { Crosshair, ZoomIn, Hand, RotateCcw } from 'lucide-react';
import { Tooltip } from '@/components/ui/tooltip';
import type { ChartMode } from './types';

// Top-right 3-button mutex "mini-keyboard" (Alexey's framing) that picks the
// interaction mode, plus a "reset to whole day" button shown only when zoomed.
// Cursors and temp-mode (shift=zoom, ctrl=pan) are owned by the chart; this is
// just the persistent selector. Tooltips use the shared fast Tooltip (not the
// native `title=` attribute, which is slow to appear) — on wide screens the
// label is also shown inline next to the icon so hover isn't needed at all.

const MODES: { mode: ChartMode; Icon: typeof Crosshair; title: string }[] = [
  { mode: 'annotate', Icon: Crosshair, title: 'Разметка' },
  { mode: 'zoom', Icon: ZoomIn, title: 'Масштаб' },
  { mode: 'pan', Icon: Hand, title: 'Перемещение' },
];

export interface ModeKeypadProps {
  mode: ChartMode;
  onModeChange: (m: ChartMode) => void;
  /** Whether the chart is zoomed in (shows the reset button). */
  zoomed: boolean;
  onReset: () => void;
  /** The mode temporarily forced by a held modifier (shift/ctrl), for the
   *  pressed-state ring — so the operator sees the temp switch. */
  tempMode?: ChartMode | null;
  /** Desktop (2026-09 correction): marquee-zoom is the chart's ONLY
   *  interaction, so the annotate/zoom/pan picker has nothing to pick
   *  between — hide it and show just the "весь день" reset button. Mobile
   *  keeps the full 3-mode keypad unchanged. */
  hideModes?: boolean;
}

export function ModeKeypad({ mode, onModeChange, zoomed, onReset, tempMode, hideModes }: ModeKeypadProps) {
  return (
    <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
      {zoomed && (
        <Tooltip content="Сброс масштаба — весь день" openDelay={100}>
          <button
            type="button"
            onClick={onReset}
            className="mr-1 inline-flex items-center gap-1 rounded-md border border-border bg-card/90 px-2 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur hover:text-foreground"
          >
            <RotateCcw className="size-3.5" />
            <span className="hidden lg:inline">Весь день</span>
          </button>
        </Tooltip>
      )}
      {!hideModes && (
      <div className="inline-flex overflow-hidden rounded-md border border-border bg-card/90 shadow-sm backdrop-blur">
        {MODES.map(({ mode: m, Icon, title }, i) => {
          const active = (tempMode ?? mode) === m;
          const isTemp = tempMode === m;
          return (
            <Tooltip key={m} content={title} openDelay={100}>
              <button
                type="button"
                aria-pressed={active}
                aria-label={title}
                onClick={() => onModeChange(m)}
                className={[
                  'inline-flex items-center justify-center gap-1 p-1.5 transition-colors',
                  i > 0 ? 'border-l border-border' : '',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  isTemp ? 'ring-1 ring-inset ring-primary/60' : '',
                ].join(' ')}
              >
                <Icon className="size-4" />
                <span className="hidden text-xs lg:inline">{title}</span>
              </button>
            </Tooltip>
          );
        })}
      </div>
      )}
    </div>
  );
}
